import { FlexColumn, FlexRow, StyledBanner, StyledButton, useLoading } from '@gorila-shared-ui/components';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'baseui/modal';
import { HeadingXSmall, LabelMedium } from 'baseui/typography';
import { memo, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useFeedback } from '../../hooks/useFeedback';
import { useStyles } from '../../hooks/useStyles';
import { getDeviceCommands, postCommand } from '../../services/commandService';
import { Asset } from '../../types/asset';
import { AvailableCommand, DeviceCommands } from '../../types/availableCommand';
import { getAssetTitle } from '../../utils/assets';
import LabeledSelect from '../ui/LabeledSelect';

type Props = {
  onClose: () => void;
  isOpen: boolean;
  asset: Asset;
  label?: string;
};
function CommandModal({ onClose, isOpen, asset, label }: Props) {
  const [deviceSelected, setDeviceSelected] = useState<DeviceCommands>();
  const [commandSelected, setCommandSelected] = useState<AvailableCommand>();
  const [devices, setDevices] = useState<DeviceCommands[]>();
  const { startLoading, stopLoading, loading } = useLoading();
  const { startLoading: startLoadingDevices, stopLoading: stopLoadingDevices, loading: loadingDevices } = useLoading();
  const [error, setError] = useState('');
  const { theme, classes } = useStyles();
  const { showPositiveFeedback, showFailFeedback } = useFeedback();

  const loadDeviceCommands = async () => {
    if (!asset._id) return;
    startLoadingDevices();
    const { devices, error } = await getDeviceCommands(asset._id);
    if (!error && devices) {
      setDevices(devices);
      const initialDevice = devices[0];
      if (initialDevice) {
        setDeviceSelected(initialDevice);
        const initialCommand = initialDevice.commands[0];
        if (initialCommand) {
          setCommandSelected(initialCommand);
        }
      }
    } else {
      showFailFeedback(error || 'Ocurrió un error, inténtalo más tarde.');
    }
    stopLoadingDevices();
  };

  useEffect(() => {
    if (!isOpen) {
      setDevices(undefined);
      return;
    }

    loadDeviceCommands();
    return () => {
      resetModal();
    };
  }, [asset, isOpen]);

  const sendCommand = async () => {
    if (!deviceSelected || !commandSelected?.commandName || !asset._id) return;
    startLoading();
    const { commandId, error } = await postCommand(asset._id, deviceSelected.ident, commandSelected.commandName);
    if (!error && commandId) {
      showPositiveFeedback('El comando ha sido enviado');
      resetModal();
    } else if (typeof error === 'string') {
      setError(error);
    } else {
      showFailFeedback(error || 'Ocurrió un error, inténtalo más tarde.');
    }
    stopLoading();
  };

  const resetModal = () => {
    onClose();
    setDeviceSelected(undefined);
    setCommandSelected(undefined);
  };

  return createPortal(
    <Modal
      animate
      closeable
      onClose={resetModal}
      isOpen={isOpen}
    >
      <ModalHeader>
        <FlexRow
          gap={theme.sizing.scale200}
          classNames={classes.centeredStart}
        >
          <HeadingXSmall margin={0}>Enviar comando</HeadingXSmall>
        </FlexRow>
      </ModalHeader>
      <ModalBody>
        <FlexColumn>
          <LabelMedium>A: {label ?? getAssetTitle(asset)}</LabelMedium>
          <LabeledSelect
            label="Dispositivo:"
            options={devices?.map((device) => ({ ...device, label: `${device.model} - ${device.ident}` }))}
            disabled={!devices || devices.length === 1}
            value={[{ ...deviceSelected }]}
            onChange={(params) => {
              const option = params.option as DeviceCommands;
              setDeviceSelected(option);
              const initialCommand = option.commands[0];
              if (initialCommand) {
                setCommandSelected(initialCommand);
              }
            }}
            valueKey="ident"
            loading={loadingDevices}
          />
          <LabeledSelect
            label="Comando:"
            options={deviceSelected?.commands}
            disabled={!deviceSelected?.commands.length}
            value={[{ ...commandSelected }]}
            onChange={(params) => setCommandSelected(params.option as AvailableCommand)}
            labelKey="commandDescription"
            valueKey="commandDescription"
            loading={loadingDevices}
          />
          {error && (
            <StyledBanner
              title="*Error*"
              kind="negative"
              action={{
                label: 'Label',
                onClick: () => {
                  setError('');
                },
              }}
            >
              {error}
            </StyledBanner>
          )}
        </FlexColumn>
      </ModalBody>
      <ModalFooter>
        <FlexRow
          gap={theme.sizing.scale300}
          classNames={classes.centeredEnd}
        >
          <StyledButton
            kind="tertiary"
            onClick={resetModal}
          >
            Cancelar
          </StyledButton>
          <StyledButton
            onClick={sendCommand}
            disabled={!commandSelected || !deviceSelected}
            isLoading={loading}
          >
            Enviar
          </StyledButton>
        </FlexRow>
      </ModalFooter>
    </Modal>,
    document.body
  );
}

export default memo(CommandModal);
